import React, {useCallback, useEffect, useMemo, useState} from 'react';
import usePageTitle from '~/app/hooks/usePageTitle';
import {useAppHeader} from '~/app/modules/appHeader';
import List from './List';
import useTranslation from "~/app/hooks/useTranslation";
import {useStoreActions, useStoreState} from '~/store';
import Header from './Header';
import Footer from './Footer';
import {useParams} from 'react-router-dom';
import {Project} from "~/types";
import {route} from "../app/helpers/route";
import {MACHINES_ROUTES} from "../machines/Router";
import EmptyMessage from './EmptyMessage'
import {ListProps} from "~/machines/Details/List";
import AddIssue, {AddIssueProps} from '~/issues/Add';
import Quantity from './Dialog/Quantity';
import Message from './Message';
import classes from './style.module.scss';
import moment from 'moment';
import {InspectionTypes} from "~/periodicCheck/store";
import {SOURCE_ISSUE_TYPE} from "~/app/data/statuses/issue";
import { OperatingTypes } from '~/app/data/statuses/operatingData';
import {MACHINES_DETAILS_ROUTES} from "~/machines/Details/Router";

export type PeriodicCheckComponent = React.FC<Pick<ListProps, 'onSelect'>>

const PeriodicCheck: PeriodicCheckComponent = () => {
    const {t} = useTranslation("periodicCheck");

    const {setData} = useAppHeader();
    const {projectId} = useParams<{ projectId: Project['id'] }>();
    const machine = useStoreState(state => state.machine.data);
    const checkEquipment = useStoreState(state => state.periodicCheck.checkEquipment);
    const headerSubTitle = useMemo(() => {
        if (checkEquipment?.id) {
            const selected =
                String(machine.id) === String(checkEquipment.id)
                    ? machine
                    : machine.linkedMachines?.find(l => String(l.id) === String(checkEquipment.id)) ?? checkEquipment;
            const line = [selected?.intern, selected?.name].filter(Boolean).join(' - ').toUpperCase();
            if (line) {
                return line;
            }
        }
        return [machine?.intern, machine?.name].filter(Boolean).join(' - ').toUpperCase();
    }, [checkEquipment, machine]);
    const updateCheckData = useStoreActions(actions => actions.periodicCheck.updateCheckData);
    const updateCurrentCheck = useStoreActions(actions => actions.periodicCheck.updateCurrentCheck);
    const checksData = useStoreState(state => state.periodicCheck.checksData);
    const inspectionType = useStoreState(state => state.periodicCheck.inspectionType);
    const [issueType, setIssueType] = useState<AddIssueProps['type']>('issue');
    const [openAddIssueDialog, setAddIssueDialog] = useState<boolean>(false);
    const [openQuantityDialog, setOpenQuantityDialog] = useState<boolean>(false);

    usePageTitle(t(`${inspectionType}.title`));

    useEffect(() => {
        setData({
            title: t(`${inspectionType}.title`),
            subTitle: headerSubTitle,
            backLink: inspectionType !== InspectionTypes.ACCEPTANCE_INSPECTION && machine.linkedMachines && machine.linkedMachines.length > 0
                ? route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, { id: machine.id, projectId })
                : route(MACHINES_ROUTES.details, { id: machine.id, projectId }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false
        });
    }, [setData, machine, headerSubTitle, t, projectId, inspectionType]);

    const toggleQuantityDialog = useCallback(() => {
        openQuantityDialog && updateCurrentCheck(null);
        setOpenQuantityDialog(!openQuantityDialog);
    }, [setOpenQuantityDialog, openQuantityDialog, updateCurrentCheck]);

    const toggleAddIssueDialog = useCallback(() => {
        openAddIssueDialog && updateCurrentCheck(null);
        setAddIssueDialog(!openAddIssueDialog);
    }, [setAddIssueDialog, openAddIssueDialog, updateCurrentCheck]);

    const changeStatus = useCallback((check, status) => {
        updateCurrentCheck(check);
        if (status === 2) {
            setIssueType('issue');
            toggleAddIssueDialog();
        } else if (check.fluidTypeId) {
            toggleQuantityDialog();
        } else {
            check.status = status;
            check.quantity = 0;
            updateCheckData({...check});
            updateCurrentCheck(null);
        }
    }, [setIssueType, toggleAddIssueDialog, toggleQuantityDialog, updateCheckData, updateCurrentCheck]);

    const showRegisterOperatingTimeMessage = useMemo(() => {
        return [InspectionTypes.DAILY, InspectionTypes.PERIODIC].includes(inspectionType)
            && !moment(machine.lastOperatingTimeDate).isSame(moment(), 'day')
            && !!machine.operatingTimeAllowed
    }, [machine.lastOperatingTimeDate, machine.operatingTimeAllowed, inspectionType]);

    const showRegisterOperatingCountMessage = useMemo(() => {
        return [InspectionTypes.DAILY, InspectionTypes.PERIODIC].includes(inspectionType)
            && !moment(machine.lastOperatingCountDate).isSame(moment(), 'day')
            && !!machine.operatingCountAllowed
    }, [machine.operatingCountAllowed, machine.lastOperatingCountDate, inspectionType]);

    if (!checksData.length) {
        return <EmptyMessage/>;
    }

    return (
        <>
            {showRegisterOperatingTimeMessage && <Message type={machine.categoryType} operatingUnitType={OperatingTypes.OPERATING_TIME} />}
            {showRegisterOperatingCountMessage && <Message type={machine.categoryType} operatingUnitType={OperatingTypes.OPERATING_COUNT} />}
            <div className={showRegisterOperatingTimeMessage || showRegisterOperatingCountMessage ? classes['disabled'] : ''}>
                <Header/>
                <List list={checksData} onSelect={changeStatus}/>
            </div>
            <Footer/>
            <AddIssue
                open={openAddIssueDialog}
                onClose={toggleAddIssueDialog}
                type={issueType}
                inspectionType={inspectionType}
                sourceTypeId={SOURCE_ISSUE_TYPE.DAILY_CHECK}
                allowAddMore={true}
            />
            <Quantity open={openQuantityDialog} onClose={toggleQuantityDialog}/>
        </>
    );
};

export default PeriodicCheck
